import { CanvasEdge } from "../models/CanvasEdge.ts";
import { CanvasNode, CanvasNodeData } from "../models/CanvasNode.ts";
import { isNoReplyHandle } from "./BodyLinkRules.ts";

// A node's plain body-level connection (no explicit handle id) means one of two things:
// auto-continue into the next node, or a "no reply within N minutes" timeout branch into a
// Smart Delay. If the node gains real buttons/items and that edge points anywhere else, it's
// stale leftover data from before — remove it. If it points at a Smart Delay, keep it: that's
// the intentional timeout wire, and the backend only honours it if nothing else wins first.
export function pruneStaleBodyEdge(
    nodeId: string,
    edges: CanvasEdge[],
    nodes: CanvasNode[],
    setEdges: (edges: CanvasEdge[]) => void
) {
    const isBodyEdge = (e: CanvasEdge) => e.source === nodeId && (!e.sourceHandle || e.sourceHandle === nodeId);
    const bodyEdge = edges.find(isBodyEdge);
    if (!bodyEdge) return;

    const targetNode = nodes.find((n) => (n.data as CanvasNodeData).id === bodyEdge.target);
    const targetType = (targetNode?.data as CanvasNodeData)?.type;
    if (targetType !== "SMART_DELAY") {
        setEdges(edges.filter((e) => !isBodyEdge(e)));
    }
}

// The no-reply connector is keyed to one template, so swapping or removing that template leaves an
// edge whose handle no longer renders — invisible on the canvas but still saved, and still matched
// by the backend. Drop every no-reply edge on this node except the one the handle still exists for.
export function pruneNoReplyEdges(
    flowStartNodeId: string | undefined,
    keepHandle: string | null,
    edges: CanvasEdge[],
    setEdges: (edges: CanvasEdge[]) => void
) {
    if (!flowStartNodeId) return;
    const isStale = (e: CanvasEdge) =>
        e.source === flowStartNodeId && isNoReplyHandle(e.sourceHandle) && e.sourceHandle !== keepHandle;
    if (!edges.some(isStale)) return;
    setEdges(edges.filter((e) => !isStale(e)));
}
